/**
 * COACH-DEMO-UNIFICATION-1 — unified coach/demo/training schema (Zod v4).
 *
 * Covers five Firestore collections introduced by this session:
 *   coach_workflows   — workflow definitions (mode-discriminated)
 *   coach_concepts    — concept card definitions (replaces archimedes_concepts)
 *   coach_sessions    — unified session telemetry
 *   coach_feedback    — unanswered Qs / stall signals
 *   coach_provenance  — per-response AEGIS source chain (wired in COACH-PROVENANCE-1)
 *
 * Zod v4 rules:
 *   - z.record requires two args: z.record(z.string(), z.any())
 *   - All doc schemas use .passthrough() for forward compat
 *   - Enums: frozen constant + z.enum(Object.values(…))
 *   - Additive schema changes use .optional() first
 */
import { z } from 'zod';

// ── Mode discriminator ─────────────────────────────────────────────────

export const COACH_WORKFLOW_MODES = Object.freeze(['demo', 'onboarding', 'training']);
export const CoachWorkflowModeSchema = z.enum(COACH_WORKFLOW_MODES);

// ── Audience ───────────────────────────────────────────────────────────

export const COACH_WORKFLOW_AUDIENCES = Object.freeze([
  'internal-rep', 'external-prospect', 'tenant-user', 'tenant-admin',
]);
export const CoachWorkflowAudienceSchema = z.enum(COACH_WORKFLOW_AUDIENCES);

// ── Runtime ────────────────────────────────────────────────────────────

export const COACH_WORKFLOW_RUNTIMES = Object.freeze(['ambient', 'guided', 'chat']);
export const CoachWorkflowRuntimeSchema = z.enum(COACH_WORKFLOW_RUNTIMES);

// ── Session status ─────────────────────────────────────────────────────

export const COACH_SESSION_STATUSES = Object.freeze(['active', 'paused', 'completed', 'abandoned']);
export const CoachSessionStatusSchema = z.enum(COACH_SESSION_STATUSES);

// ── Step schema ────────────────────────────────────────────────────────

export const CoachWorkflowStepSchema = z.object({
  stepId:         z.string().min(1),
  title:          z.string().min(1).max(200),
  body:           z.string().max(2000).optional(),
  targetSelector: z.string().optional(),  // CSS selector or route for guided steps
  expectedAction: z.string().optional(),  // action type the engine waits for
  conceptId:      z.string().optional(),  // linked CoachConcept
  role:           z.string().optional(),  // step gated to this role (ARCHIMEDES-3)
  skipAllowed:    z.boolean().default(true),
}).passthrough();

// ── Personalization ────────────────────────────────────────────────────

export const CoachPersonalizationSchema = z.object({
  company:  z.string().optional(),
  industry: z.string().optional(),
  vertical: z.string().optional(),
  role:     z.string().optional(),
}).passthrough();

// ── Mode variants (optional: same workflowId, per-mode step override) ─

export const CoachWorkflowModeVariantSchema = z.object({
  workflowId: z.string().min(1),
  steps:      z.array(CoachWorkflowStepSchema).optional(),
}).passthrough();

// ── coach_workflows document ───────────────────────────────────────────

export const CoachWorkflowSchema = z.object({
  workflowId:      z.string().min(1),
  tenantId:        z.string().min(1),
  mode:            CoachWorkflowModeSchema,
  audience:        CoachWorkflowAudienceSchema,
  role:            z.string().nullable().optional(),
  runtime:         CoachWorkflowRuntimeSchema,
  module:          z.string().optional(),   // owning module (e.g. 'tms', 'wms')
  title:           z.string().min(1).max(200).optional(),
  description:     z.string().max(1000).optional(),
  steps:           z.array(CoachWorkflowStepSchema).default([]),
  personalization: CoachPersonalizationSchema.optional(),
  modeVariants:    z.record(z.string(), CoachWorkflowModeVariantSchema).optional(),
  migratedFrom:    z.string().nullable().optional(),
  isDeleted:       z.boolean().default(false),
  schemaVersion:   z.number().int().default(1),
}).passthrough();

// ── coach_concepts document ────────────────────────────────────────────

export const CoachConceptSchema = z.object({
  conceptId:    z.string().min(1),
  tenantId:     z.string().min(1),
  label:        z.string().min(1).max(200),
  shortDef:     z.string().min(1).max(500),
  longDef:      z.string().max(3000).optional(),
  terminology:  z.array(z.string()).default([]),
  modules:      z.array(z.string()).default([]),
  isDeleted:    z.boolean().default(false),
  schemaVersion: z.number().int().default(1),
}).passthrough();

// ── coach_sessions step history entry ─────────────────────────────────

export const CoachSessionStepHistoryEntrySchema = z.object({
  stepId:         z.string().min(1),
  enteredAt:      z.string(),
  completedAt:    z.string().optional(),
  observedAction: z.record(z.string(), z.any()).optional(),
  status:         z.enum(['active', 'completed', 'skipped']),
}).passthrough();

// ── coach_sessions deviation entry ────────────────────────────────────

export const CoachSessionDeviationSchema = z.object({
  stepId:         z.string().nullable(),
  type:           z.string(),
  observedAction: z.record(z.string(), z.any()).nullable().optional(),
  adaptation:     z.string().nullable().optional(),
  timestamp:      z.string(),
}).passthrough();

// ── coach_sessions document ────────────────────────────────────────────

export const CoachSessionSchema = z.object({
  sessionId:        z.string().min(1),
  tenantId:         z.string().min(1),
  userId:           z.string().min(1),
  workflowId:       z.string().min(1),
  mode:             CoachWorkflowModeSchema,
  status:           CoachSessionStatusSchema,
  currentStepIndex: z.number().int().min(0).default(0),
  currentStepId:    z.string().nullable(),
  startedAt:        z.string(),
  lastActivityAt:   z.string(),
  completedAt:      z.string().nullable().optional(),
  abandonedAt:      z.string().nullable().optional(),
  abandonReason:    z.string().nullable().optional(),
  pausedAt:         z.string().nullable().optional(),
  resumedAt:        z.string().nullable().optional(),
  modeSwitchedAt:   z.string().nullable().optional(),
  previousMode:     CoachWorkflowModeSchema.optional(),
  stepHistory:      z.array(CoachSessionStepHistoryEntrySchema).default([]),
  deviations:       z.array(CoachSessionDeviationSchema).default([]),
  isDeleted:        z.boolean().default(false),
}).passthrough();

// ── coach_feedback document ────────────────────────────────────────────

export const CoachFeedbackSchema = z.object({
  feedbackId:  z.string().min(1),
  tenantId:    z.string().min(1),
  userId:      z.string().min(1),
  sessionId:   z.string().nullable().optional(),
  workflowId:  z.string().nullable().optional(),
  stepId:      z.string().nullable().optional(),
  question:    z.string().nullable().optional(),
  type:        z.enum(['unanswered_question', 'stall', 'error', 'other']).default('unanswered_question'),
  submittedAt: z.string(),
  isDeleted:   z.boolean().default(false),
}).passthrough();

// ── coach_provenance document (wired in COACH-PROVENANCE-1) ───────────

export const CoachProvenanceEntrySchema = z.object({
  conceptId:    z.string().optional(),
  field:        z.string().optional(),
  source:       z.string().optional(),
  confidence:   z.number().min(0).max(1).optional(),
}).passthrough();

export const CoachProvenanceSchema = z.object({
  provenanceId: z.string().min(1),
  tenantId:     z.string().min(1),
  sessionId:    z.string().nullable().optional(),
  workflowId:   z.string().nullable().optional(),
  stepId:       z.string().nullable().optional(),
  model:        z.string().optional(),   // e.g. 'haiku' | 'sonnet'
  confidence:   z.number().min(0).max(1).optional(),
  sources:      z.array(CoachProvenanceEntrySchema).default([]),
  missingDataFlags: z.array(z.string()).default([]),
  recordedAt:   z.string(),
  isDeleted:    z.boolean().default(false),
}).passthrough();
